import { Avatar, Badge, Box, CircularProgress } from "@mui/material";
import React, { Component } from "react";
import Theme from "../common/Theme";
import VacancyIcon from "../resources/icons-user.svg";
import ComplianceBadge from "./ComplianceBadge";
import Tooltip from "./Tooltip";

interface ProfilePictureProps {
  user: any;
  updating?: boolean;
  size: keyof typeof ProfilePicture.size;
  updateCache?: boolean;
  // When defined, use this as the <img> src instead of computing it from user.getProfilePictureUrl().
  // A blob object URL (from CurrentUserAvatar) renders regardless of Content-Type and sidesteps the
  // cross-origin ORB block that a raw API URL in <img> hits. `null` forces the initials fallback
  // (e.g. while the malware scan is still processing). `undefined` keeps the legacy behaviour.
  imageUrl?: string | null;
  onClick?: () => void;
  sx?: object;
  tooltip?: string;
  badgeContent?: React.ReactNode;
  showCompliance?: boolean;
  complianceBadgeSize?: string | number;
  complianceBadgeMargin?: string;
  showTooltip?: boolean;
}

export default class ProfilePicture extends Component<ProfilePictureProps> {
  static size = { xs: "32px", sm: "40px", md: "44px", lg: "64px", xl: "80px" };
  static fontSize = {
    xs: "14px",
    sm: "16px",
    md: "16px",
    lg: "18px",
    xl: "20px",
  };

  static defaultProps = {
    size: "xl",
    updateCache: false,
    showCompliance: false,
    showTooltip: true,
  };

  renderAvatar() {
    const { user, updating, size, updateCache, onClick, sx, imageUrl } = this.props;

    // imageUrl override (CurrentUserAvatar): use it verbatim. Legacy path (imageUrl undefined):
    // compute from the user and mint a cache-bust timestamp when updateCache is set.
    const usingOverride = imageUrl !== undefined;
    const src = usingOverride
      ? imageUrl
      : user
        ? user?.getProfilePictureUrl(updateCache ? new Date() : null)
        : null;
    const showPic = usingOverride
      ? !!imageUrl && !updating
      : user && user.hasProfilePicture && !updating;

    return (
      <Avatar
        src={showPic ? src : null}
        sx={{
          width: ProfilePicture.size[size],
          height: ProfilePicture.size[size],
          background: Theme.getStyleVar("--prussian-blue-pb-500"),
          ...sx,
        }}
        onClick={onClick}
      >
        {updating ? (
          <CircularProgress />
        ) : !user || user.isVacancy ? (
          <div>
            <VacancyIcon />
          </div>
        ) : (
          !showPic && (
            <Box
              className="klayo_profilepic-initials"
              sx={{ fontSize: ProfilePicture.fontSize[size] }}
            >
              {user.getInitials()}
            </Box>
          )
        )}
      </Avatar>
    );
  }

  renderAvatarWithTooltip() {
    const { user, tooltip, showTooltip } = this.props;

    return (tooltip || user) && showTooltip ? (
      <Tooltip title={tooltip ? tooltip : user ? user.fullName : null}>
        {this.renderAvatar()}
      </Tooltip>
    ) : (
      this.renderAvatar()
    );
  }

  render() {
    const {
      user,
      size,
      sx,
      badgeContent,
      showCompliance,
      complianceBadgeSize,
      complianceBadgeMargin,
      showTooltip,
    } = this.props;

    return (
      <div
        className={
          "klayo_profilepic klayo_profilepic--size-" +
          size +
          (!user || user.isVacancy ? " klayo_profilepic-vacancy" : "")
        }
        style={{
          width: ProfilePicture.size[size],
          height: ProfilePicture.size[size],
          backgroundColor: Theme.getStyleVar("--prussian-blue-pb-500"),
          ...sx,
        }}
      >
        {showCompliance ? (
          <ComplianceBadge
            isCompliant={user.isCompliant}
            size={complianceBadgeSize}
            margin={complianceBadgeMargin}
            showTooltip={showTooltip}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            sx={{
              width: ProfilePicture.size[size],
              height: ProfilePicture.size[size],
            }}
          >
            {this.renderAvatar()}
          </ComplianceBadge>
        ) : (
          <Badge
            badgeContent={badgeContent}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            sx={{
              width: ProfilePicture.size[size],
              height: ProfilePicture.size[size],
            }}
          >
            {this.renderAvatar()}
          </Badge>
        )}
      </div>
    );
  }
}
